import { Box, CardActionArea, CardMedia, Grid, Stack } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcClickable, MprcIconButtonMoreOptionsDropdown, MprcLink } from "components";
import { MprcCardTypography } from 'components/MprcCardTypography';
import { CardAction, CardActionLocation, CardActionType, CardDto, CardTextVariant, CreateEnrollResults, EnrollStatus, ShareTrainingTarget, TrainingUserInteractionOption } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';
import { discoverPath, ICardDtoToCardRibbonOptions, RenderCardIcon, RenderCardSticker, RenderCardText } from 'views/general/Cards/Common';
import { DoTrainingBoomark } from 'views/lms/components/TrainingBookmarkButton';
import { TrainingShareDialog } from 'views/lms/dialogs/TrainingShareDialog';
import { ConclusionFormDialog } from 'views/lms/student/EnrollmentView/ECourse/ConclusionFormDialog';
import { TrainingRegistrationDialog } from 'views/lms/student/Registration/TrainingRegistrationDialog';
import { TrainingEditRating } from 'views/lms/student/TrainingRatingView/TrainingEditRating';

///////////////////////////////////////////////////////////////////////////////

const Body = (item: CardDto) => {

    const navigate = useNavigate();
    const titleAction = item.actions.find(p => p.location == CardActionLocation.Title);

    return <Box height="100px">
        {
            item.lines.map((line, index) => {
                const titleLink = line.variant == CardTextVariant.Title && !item.disabled && titleAction ? discoverPath(titleAction, item.data)?.url : undefined;
                const textVariant = CardTextVariant[line.variant].toCamelcase() as any;

                const renderLine = (
                    <MprcCardTypography key={index} variant={textVariant}>
                        {
                            titleLink ?
                                <MprcClickable onClick={() => navigate(titleLink)}>
                                    <RenderCardText line={line} />
                                </MprcClickable>
                                : <RenderCardText line={line} />
                        }
                    </MprcCardTypography>

                );

                return line.variant == CardTextVariant.Sticker ? <Box width={150}>{renderLine}</Box> : renderLine;
            })
        }
    </Box>;
};

///////////////////////////////////////////////////////////////////////////////

const Actions = ({ item, onReload }: { item: CardDto; onReload?: () => void; }) => {
    const primaryAction = item.actions.find(p => p.location == CardActionLocation.Primary);
    const secondaryAction = item.actions.find(p => p.location == CardActionLocation.Secondary);
    const overflowActions = item.actions.filter(p => p.location == CardActionLocation.Overflow);
    const [dialog, setDialog] = useState<any>(null);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const data = item.data;

    const handleCloseRegistration = (result: CreateEnrollResults | undefined) => {
        setDialog(null);
        if (result) {
            if (result.enrollmentId && result.enrollStatus == EnrollStatus.NotStarted) {
                navigate(LmsPaths.BuildEnrollmentViewer(data.trainingType!, result.enrollmentId));
            }
            else if (onReload)
                onReload();
        }
    };

    const handleSelection = (action: CardAction) => {
        switch (action.actionType) {
            case CardActionType.LmsRegistrationOneClick:
                setDialog(<TrainingRegistrationDialog
                    trainingId={data["trainingId"]!}
                    masterEnrollmentId={data["lpEnrollmentId"]!}
                    immediateRegistration={true}
                    onClose={handleCloseRegistration}
                />);
                break;
            case CardActionType.LmsTrainingInfo:
                setDialog(<TrainingRegistrationDialog
                    trainingId={data["trainingId"]!}
                    readonly={true}
                    onClose={handleCloseRegistration}
                />);
                break;
            case CardActionType.LmsRegistration:
                setDialog(<TrainingRegistrationDialog
                    trainingId={data["trainingId"]!}
                    masterEnrollmentId={data["lpEnrollmentId"]!}
                    onClose={handleCloseRegistration}
                />);
                break;
            case CardActionType.LmsFillConclusionForm:
                setDialog(<ConclusionFormDialog trainingId={data["trainingId"]} classroomId={data["classroomId"]!} masterEnrollmentId={data["lpEnrollmentId"]!} enrollmentId={null} onClose={(result) => { setDialog(null); result != null && onReload && onReload(); }} />);
                break;
            case CardActionType.LmsEditConclusionForm:
                setDialog(<ConclusionFormDialog trainingId={data["trainingId"]} classroomId={data["classroomId"]!} enrollmentId={data["enrollmentId"]!} masterEnrollmentId={data["lpEnrollmentId"]!} onClose={(result) => { setDialog(null); result != null && onReload && onReload(); }} />);
                break;
            // Esta opção parece não fazer sentido AQUI. Não há 'publicationId' sendo retornado em 'data'.
            // case CardActionType.PublicationAddToList:
            //     setDialog(
            //         <AddToListDrawer
            //             publicationId={data["publicationId"]!}
            //             onChanged={() => onReload && onReload()}
            //             subtitle={data["title"]}
            //             onClose={() => setDialog(null)}
            //         />
            //     );
            //     break;
            case CardActionType.LmsAddToLibrary:
                setDialog(
                    <DoTrainingBoomark
                        trainingId={data["trainingId"]!}
                        option={TrainingUserInteractionOption.Bookmark}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
            case CardActionType.LmsRateAndWriteComment:
                setDialog(
                    <TrainingEditRating
                        enrollmentId={data["enrollmentId"]!}
                        trainingName={data["trainingName"]}
                        onClose={() => {
                            setDialog(null);
                        }}
                    />
                );
                break;
            case CardActionType.LmsShareInChannel:
                setDialog(
                    <TrainingShareDialog
                        shareOption={ShareTrainingTarget.Channel}
                        onClose={() => setDialog(null)}
                        trainingId={data["trainingId"]}
                        trainingName={data["trainingName"]}
                    />
                );
                break;
            case CardActionType.LmsShareInList:
                setDialog(
                    <TrainingShareDialog
                        shareOption={ShareTrainingTarget.List}
                        onClose={() => setDialog(null)}
                        trainingId={data["trainingId"]}
                        trainingName={data["trainingName"]}
                    />
                );
                break;
            case CardActionType.LmsNewConclusionForm:
                setDialog(
                    <ConclusionFormDialog
                        trainingId={data["trainingId"]}
                        classroomId={data["classroomId"]!}
                        masterEnrollmentId={data["lpEnrollmentId"]!}
                        enrollmentId={null}
                        onClose={(result) => { setDialog(null); result != null && onReload && onReload(); }}
                    />
                );
                break;
            default:
                const pathUrl = discoverPath(action, data)?.url;
                if (pathUrl) navigate(pathUrl);
                else enqueueSnackbar("Opção não disponível.", { variant: "error" });
                break;
        }
    };

    return (
        <>
            {dialog}

            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" sx={{ height: 32 }}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid>
                        {
                            primaryAction &&
                            <MprcCardTypography variant="linkAlt" fontSize={theme => theme.typography.fontSize}>
                                <RenderCardIcon icon={primaryAction.icon} variant="pre" />
                                {!item.disabled || primaryAction.actionType != CardActionType.None ?
                                    <MprcClickable onClick={() => handleSelection(primaryAction)} disabled={item.disabled}>
                                        {primaryAction.text}
                                    </MprcClickable>
                                    :
                                    primaryAction.text
                                }
                            </MprcCardTypography>
                        }
                    </Grid>
                    {
                        secondaryAction &&
                        <Grid>
                            <MprcLink href={discoverPath(secondaryAction, item.data)?.url}>
                                <MprcCardTypography variant="linkAlt">
                                    <RenderCardIcon icon={secondaryAction.icon} variant="pre" />
                                    {secondaryAction.text}
                                </MprcCardTypography>
                            </MprcLink>
                        </Grid>
                    }
                </Grid>

                {
                    overflowActions.length > 0 &&
                    <MprcIconButtonMoreOptionsDropdown
                        color="muted"
                        onChange={(selectedValue) => handleSelection({ actionType: selectedValue } as CardAction)}
                        items={overflowActions.map(a => ({ text: a.text, value: a.actionType }))}
                    />
                }
            </Stack>
        </>
    );
};

///////////////////////////////////////////////////////////////////////////////

const ThumbnailNavigate = ({ thumbnailUrl, thumbnailLink }: { thumbnailUrl: string; thumbnailLink?: string; }) => {
    const navigate = useNavigate();

    return thumbnailLink
        ? <CardActionArea onClick={() => navigate(thumbnailLink)}>
            <CardMedia
                component="img"
                width={AppImageDimension.ThumbnailWide.width}
                height={AppImageDimension.ThumbnailWide.height}
                image={thumbnailUrl}
            />
        </CardActionArea>
        :
        <CardMedia
            component="img"
            width={AppImageDimension.ThumbnailWide.width}
            height={AppImageDimension.ThumbnailWide.height}
            image={thumbnailUrl}
            sx={{ borderRadius: "8px" }}
        />;
};

///////////////////////////////////////////////////////////////////////////////

const Thumbnail = ({ item }: { item: CardDto; }) => {
    if (item.thumbnail == null) return null;

    const thumbnailAction = item.actions.find(p => p.location == CardActionLocation.Thumbnail);
    const thumbnailLink = !item.disabled && thumbnailAction != undefined ? discoverPath(thumbnailAction, item.data)?.url : undefined;
    return <ThumbnailNavigate thumbnailUrl={item.thumbnail.url} thumbnailLink={thumbnailLink} />;
};

///////////////////////////////////////////////////////////////////////////////

const RenderCardMedia = (item: CardDto) => {
    return item.stickerLeft || item.stickerRight
        ? <Box sx={{ position: "relative" }}>
            <Thumbnail item={item} />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
                <Grid container justifyContent="center" width="100%">
                    <Grid size={{ xs: 6 }}>
                        {
                            item.stickerLeft ?
                                <RenderCardSticker
                                    item={item.stickerLeft}
                                />
                                : null
                        }</Grid>
                    <Grid size={{ xs: 6 }}>
                        {
                            item.stickerRight
                                ? <RenderCardSticker
                                    item={item.stickerRight}
                                />
                                : null
                        }
                    </Grid>
                </Grid>
            </Box>
        </Box>
        : <Thumbnail item={item} />;
};

///////////////////////////////////////////////////////////////////////////////

const BuildLearnPathComponentCards = ({ item, onReload }: { item: CardDto, onReload?: () => void; }) => {
    return (
        <Grid container spacing={2} wrap="nowrap" width={"100%"}>
            <Grid sx={{ minWidth: AppImageDimension.ThumbnailWide.width }}>
                <RenderCardMedia {...item} />
            </Grid>
            <Grid size="grow">
                <Stack direction="column" justifyContent="space-between" spacing={2} sx={{ height: AppImageDimension.ThumbnailWide.height }}>
                    <Body {...item} />
                    <Actions item={item} onReload={onReload} />
                </Stack>
            </Grid>
        </Grid>
    );
};

///////////////////////////////////////////////////////////////////////////////

export function CardDtoToLearnPathCardRibbon(items: CardDto[] | undefined, options?: ICardDtoToCardRibbonOptions) {
    return items == undefined || items.length == 0
        ? null
        : items.map((item) => (<BuildLearnPathComponentCards key={item.uniqueId} item={item} onReload={options?.onCarouselReload} />));
}
